import React, { useEffect, useRef, useState } from 'react';
import apiClient from '../../utils/apiClient';

export interface EightAmChoices {
  eucharist: '' | 'A' | 'B' | 'C' | 'D' | 'EOW2';
  form: 'short' | 'long';
  pop: '' | 'form-i' | 'form-i-short' | 'form-ii' | 'form-iii' | 'form-iv' | 'form-v' | 'form-vi';
  postcommunion: '' | 'eow1' | 'eow2' | 'bcp-almighty';
  preface: string;
  /** Spoken fraction anthem id ('' = none). Printed by the 12:30 full rebuild. */
  fraction: string;
  baptism: '' | 'older' | 'younger' | 'group';
  /** First candidate's name. Kept so settings saved before multi-candidate support still load. */
  baptismName: string;
  /** First candidate's gender. Same reason. */
  baptismGender: 'm' | 'f';
  /**
   * Everyone being baptized, as "Name:g|Name:g" (g = m or f) — e.g.
   * "Yanis:f|Zabdiel:m|Liam:m". Optional: absent in settings saved before
   * multi-candidate support, where baptismName/baptismGender carry the one
   * person instead.
   */
  baptismNames?: string;
  renewal: '' | 'on';
  /** Added parts for this service only, "id@place|id@place" (AddedPartsPanel). Optional: absent in Sundays saved before 2026-10-23. */
  parts?: string;
}
export const emptyEightAmChoices: EightAmChoices = {
  eucharist: '', form: 'short', pop: '', postcommunion: '', preface: 'auto', fraction: '',
  baptism: '', baptismName: '', baptismGender: 'f', baptismNames: '', renewal: '', parts: '',
};

/** One person being baptized. A service may have any number of them. */
export interface BaptismCandidate {
  name: string;
  gender: 'm' | 'f';
}

/**
 * Pack the candidate list into the single string the export link and the saved
 * settings carry: "Yanis:f|Zabdiel:m|Liam:m". Blank rows are dropped, and ':'
 * and '|' are stripped from names so they cannot break the packing.
 */
export function encodeBaptismPeople(people: BaptismCandidate[]): string {
  return people
    .map((p) => ({ name: p.name.replace(/[:|]/g, ' ').trim(), gender: p.gender }))
    .filter((p) => p.name)
    .map((p) => `${p.name}:${p.gender}`)
    .join('|');
}

/**
 * Unpack that string. Falls back to the older single name + gender pair so
 * settings saved before multi-candidate support still load. Always returns at
 * least one (possibly empty) row, so the form has something to show.
 */
export function decodeBaptismPeople(
  packed: string,
  fallbackName = '',
  fallbackGender: 'm' | 'f' = 'f'
): BaptismCandidate[] {
  const out: BaptismCandidate[] = [];
  for (const entry of (packed || '').split('|')) {
    const idx = entry.lastIndexOf(':');
    const name = (idx === -1 ? entry : entry.slice(0, idx)).trim();
    const gender = (idx === -1 ? '' : entry.slice(idx + 1)).trim().toLowerCase();
    if (name) out.push({ name, gender: gender === 'm' ? 'm' : 'f' });
  }
  if (out.length === 0) out.push({ name: fallbackName || '', gender: fallbackGender });
  return out;
}

/**
 * Put a choice set into one canonical shape before comparing two of them.
 *
 * Choices are stored as saved, so a Sunday saved before a field existed simply
 * has no key for it. A plain JSON.stringify comparison then reports a change
 * the user never made, and "Unsaved Sunday changes" sticks on forever with the
 * export blocked behind it. Filling in the defaults, expressing an older single
 * candidate in the list form, and sorting the keys makes the comparison about
 * the values alone.
 */
export function canonicalChoices(choices: Partial<EightAmChoices> | undefined): string {
  const full: EightAmChoices = { ...emptyEightAmChoices, ...(choices || {}) };
  const people = decodeBaptismPeople(full.baptismNames || '', full.baptismName, full.baptismGender);
  full.baptismNames = encodeBaptismPeople(people);
  // The single name/gender pair is a mirror of the first row; keep it in step
  // so the two representations can never disagree.
  full.baptismName = people[0].name;
  full.baptismGender = people[0].gender;
  const keys = Object.keys(full).sort() as (keyof EightAmChoices)[];
  return JSON.stringify(keys.map((k) => [k, full[k]]));
}
type PrayerKeys = 'eucharist' | 'form' | 'pop' | 'postcommunion' | 'preface' | 'fraction';
interface Template {
  id: number; church_id: number; name: string; starts_on: string; ends_on: string; revision: number;
  choices: Pick<EightAmChoices, PrayerKeys> & { byService?: Record<string, Partial<Pick<EightAmChoices, PrayerKeys>>> };
}
interface Settings {
  service: { church_id: number; plan_church_id?: number }; templates: Template[]; template: Template | null;
  override: EightAmChoices | null; choices: EightAmChoices; supported: boolean;
}
/**
 * Which service's column of the seasonal plan this panel edits. The 8 am
 * column is the template itself; the 12:30 column stores only what differs
 * from the 8 am and follows it otherwise (2026-09-22).
 */
export type PlanColumn = '8am' | '10am' | '1230';
const COLUMN_NAMES: Record<PlanColumn, string> = { '8am': '8 am', '10am': '10 am', '1230': '12:30' };
const CHOICE_NAMES: Record<string, string> = {
  eucharist: 'Eucharistic Prayer', form: 'short/full form', pop: 'Prayers of the People',
  postcommunion: 'post-communion prayer', preface: 'Proper Preface', fraction: 'fraction anthem',
};
interface Props {
  service: { service_id: number; date: string; season: string };
  column?: PlanColumn;
  choices: EightAmChoices;
  onApply: (choices: EightAmChoices) => void;
  onReady: (ready: boolean) => void;
  onPendingChanges?: (pending: boolean) => void;
}
const messageOf = (error: any) => {
  const errorBody = error.response?.data?.error;
  return typeof errorBody === 'string' ? errorBody : errorBody?.message || 'Could not save or load the saved prayer choices. Please try again.';
};

export const EightAmTemplatePanel: React.FC<Props> = ({ service, column = '8am', choices, onApply, onReady, onPendingChanges }) => {
  const [data, setData] = useState<Settings | null>(null);
  const [selectedId, setSelectedId] = useState('');
  const [editor, setEditor] = useState<{ id?: number; revision?: number; name: string; starts_on: string; ends_on: string; before: EightAmChoices } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const alive = useRef(true);
  const endpoint = `/bulletins/indesign-settings/services/${service.service_id}?column=${column}`;
  const name = COLUMN_NAMES[column];

  const accept = (settings: Settings) => {
    setData(settings);
    setSelectedId(settings.template ? String(settings.template.id) : '');
    onApply(settings.choices);
    onReady(settings.supported);
  };
  useEffect(() => {
    alive.current = true;
    onReady(false);
    let cancelled = false;
    apiClient.get<Settings>(endpoint).then(response => {
      if (!cancelled) {
        setData(response.data);
        setSelectedId(response.data.template ? String(response.data.template.id) : '');
        onApply(response.data.choices);
        onReady(response.data.supported);
      }
    }).catch(err => { if (!cancelled) setError(messageOf(err)); });
    return () => { cancelled = true; alive.current = false; };
  }, [endpoint, onApply, onReady]);

  const run = async (action: () => Promise<void>) => {
    setBusy(true); setError(''); setNotice(''); onReady(false);
    try { await action(); }
    catch (err) { if (alive.current) { setError(messageOf(err)); onReady(!!data?.supported && !editor); } }
    finally { if (alive.current) setBusy(false); }
  };
  const begin = (template?: Template, copy = false) => {
    setEditor({
      ...(template && !copy ? { id: template.id, revision: template.revision } : {}),
      name: template ? `${template.name}${copy ? ' — copy' : ''}` : `${service.season} ${service.date.slice(0, 4)}`,
      starts_on: template && !copy ? template.starts_on : service.date,
      ends_on: template && !copy ? template.ends_on : '', before: choices,
    });
    if (template) {
      const { byService, ...base } = template.choices;
      onApply({ ...emptyEightAmChoices, ...base });
    }
    onReady(false); setNotice(''); setError('');
  };
  const chosen = data?.templates.find(t => String(t.id) === selectedId);
  const dirty = !!data && canonicalChoices(choices) !== canonicalChoices(data.choices);
  useEffect(() => { onPendingChanges?.(dirty || !!editor || busy); }, [dirty, editor, busy, onPendingChanges]);

  return <div className="eight-am-template-panel">
    <h4>{name} seasonal prayers</h4>
    {error && <p className="error-message" role="alert">{error}</p>}
    {notice && <p role="status">{notice}</p>}
    {!data && !error && <p role="status">Loading saved prayer choices…</p>}
    {!data && error && <button type="button" onClick={() => run(async () => {
      const response = await apiClient.get<Settings>(endpoint); if (alive.current) accept(response.data);
    })}>Retry</button>}
    {data && !data.supported && <p>This is Morning Prayer. Use the separate Morning Prayer bulletin workflow; Eucharistic templates do not apply.</p>}
    {data?.supported && <>
      <p><strong>{data.override ? 'Using saved choices for this Sunday' : data.template ? `Using: ${data.template.name}` : 'No seasonal template for this date'}</strong>
        {data.template && <> · {data.template.starts_on} through {data.template.ends_on}</>}
        {column !== '8am' && data.template && !data.override && <> · {
          Object.keys(data.template.choices.byService?.[column] || {}).length
            ? `own ${name} choice for: ${Object.keys(data.template.choices.byService?.[column] || {}).map(k => CHOICE_NAMES[k] || k).join(', ')}; the rest follow the 8 am`
            : 'follows the 8 am choices'}</>}
      </p>
      {!editor && <>
        <p>Change the prayer selections below for this Sunday. Save a template to reuse them across a season.</p>
        {dirty && <p role="status">Unsaved Sunday changes. Export uses the choices shown below.</p>}
        <div className="template-actions">
          <button type="button" disabled={busy} onClick={() => run(async () => {
            const response = await apiClient.put<Settings>(endpoint, choices);
            if (alive.current) { accept(response.data); setNotice('Choices saved for this Sunday only.'); }
          })}>Save this Sunday only</button>
          <button type="button" disabled={busy || (!data.override && !dirty)} onClick={() => run(async () => {
            const response = await apiClient.delete<Settings>(endpoint);
            if (alive.current) { accept(response.data); setNotice('Using seasonal defaults again.'); }
          })}>Reset to seasonal defaults</button>
          {column === '8am' && <button type="button" disabled={busy} onClick={() => begin()}>Save as seasonal template</button>}
          {column !== '8am' && data.template && <>
            <button type="button" disabled={busy} onClick={() => run(async () => {
              const { eucharist, form, pop, postcommunion, preface, fraction } = choices;
              await apiClient.put(`/bulletins/indesign-settings/templates/${data.template!.id}/columns/${column}`,
                { choices: { eucharist, form, pop, postcommunion, preface, fraction }, revision: data.template!.revision });
              const response = await apiClient.get<Settings>(endpoint);
              if (alive.current) { accept(response.data); setNotice(`Saved as the ${name} choices for ${data.template!.name}. Anything the same as the 8 am keeps following it.`); }
            })}>Save as this season's {name} choices</button>
            <button type="button" disabled={busy || !data.template.choices.byService?.[column]} onClick={() => run(async () => {
              await apiClient.delete(`/bulletins/indesign-settings/templates/${data.template!.id}/columns/${column}?revision=${data.template!.revision}`);
              const response = await apiClient.get<Settings>(endpoint);
              if (alive.current) { accept(response.data); setNotice(`The ${name} follows the 8 am choices again for this season.`); }
            })}>Follow the 8 am again</button>
          </>}
        </div>
        {column !== '8am' && <p>Seasons are created and their dates changed from the 8:00 AM bulletin. Here you choose only where the {name} differs.</p>}
        {column === '8am' && data.templates.length > 0 && <div className="template-actions">
          <label>Manage template <select value={selectedId} onChange={e => setSelectedId(e.target.value)}>
            <option value="">Choose a template</option>
            {data.templates.map(t => <option key={t.id} value={t.id}>{t.name} ({t.starts_on} – {t.ends_on})</option>)}
          </select></label>
          <button type="button" disabled={busy || !chosen} onClick={() => begin(chosen)}>Edit template</button>
          <button type="button" disabled={busy || !chosen} onClick={() => begin(chosen, true)}>Copy for another season</button>
        </div>}
      </>}
      {editor && <div className="template-editor">
        <p><strong>{editor.id ? 'Edit seasonal template' : 'Create seasonal template'}</strong> — adjust the prayer selections below, then save here. Baptism and renewal choices are never included in a seasonal template.</p>
        <label>Name <input value={editor.name} maxLength={120} onChange={e => setEditor({ ...editor, name: e.target.value })} /></label>
        <label>First date <input type="date" value={editor.starts_on} onChange={e => setEditor({ ...editor, starts_on: e.target.value })} /></label>
        <label>Last date <input type="date" min={editor.starts_on} value={editor.ends_on} onChange={e => setEditor({ ...editor, ends_on: e.target.value })} /></label>
        <div className="template-actions">
          <button type="button" disabled={busy || !editor.name.trim() || !editor.starts_on || !editor.ends_on || editor.ends_on < editor.starts_on} onClick={async () => {
            setBusy(true); setError('');
            try {
              const { eucharist, form, pop, postcommunion, preface, fraction } = choices;
              const body = { church_id: data.service.plan_church_id ?? data.service.church_id, name: editor.name.trim(), starts_on: editor.starts_on, ends_on: editor.ends_on,
                choices: { eucharist, form, pop, postcommunion, preface, fraction }, ...(editor.id ? { revision: editor.revision } : {}) };
              if (editor.id) await apiClient.put(`/bulletins/indesign-settings/templates/${editor.id}`, body);
              else await apiClient.post('/bulletins/indesign-settings/templates', body);
              const response = await apiClient.get<Settings>(endpoint);
              if (alive.current) { setEditor(null); accept(response.data); setNotice('Seasonal template saved. Saved exports are unchanged.'); }
            } catch (err) { if (alive.current) setError(messageOf(err)); }
            finally { if (alive.current) setBusy(false); }
          }}>{busy ? 'Saving…' : 'Save template'}</button>
          <button type="button" disabled={busy} onClick={() => { onApply(editor.before); setEditor(null); onReady(true); }}>Cancel</button>
        </div>
      </div>}
    </>}
  </div>;
};
